import type { Locale } from './types';

/**
 * Master message dictionary. Adding a new key:
 *  1. Add it once to the `en_US` map (the reference shape).
 *  2. Add the same key to every other locale — the {@link MessageKey} type
 *     forces compile-time coverage.
 *  3. Reference it via `t('the.new.key')`.
 *
 * `{var}` placeholders are interpolated by {@link translate}; pass values
 * via the second `t()` argument. Unknown keys return the key itself, which
 * keeps the bundle from crashing on a typo (the missing translation just
 * surfaces as the raw key in the UI).
 */
const en_US = {
    // ── State cards ────────────────────────────────────────────────────
    'state.idle.detail': 'No preview context — bundle mounted but waiting for data.',
    'state.loading': 'Loading component …',
    'state.error.title': 'Component could not be loaded',
    'state.error.title_with_status': 'Component could not be loaded (HTTP {status})',
    'state.error.token_expired': 'Token may have expired — reload the preview.',

    // ── Fallback titles ────────────────────────────────────────────────
    'fallback.component_title': 'Component {id}',
    'fallback.group_title': 'Group {id}',

    // ── Group / characteristic ─────────────────────────────────────────
    'group.empty': 'No groups in this component.',
    'group.no_characteristics': 'No characteristics in this group.',
    'characteristic.required_aria': 'Required field',
    'characteristic.more_info_aria': 'More information',
    'characteristic.show_info': 'Show details',
    'characteristic.close': 'Close',
    'characteristic.no_values': 'No values available.',
    'characteristic.no_images': 'No images configured.',
    'characteristic.no_options': 'No options.',
    'characteristic.no_content': 'No content.',
    'characteristic.aria_select': 'Selection',
    'characteristic.quantity': 'Quantity',
    'characteristic.aria_quantity': '{label} quantity',

    // ── Navigation ─────────────────────────────────────────────────────
    'nav.tabs_aria': 'Groups',
    'nav.wizard_step': 'Step {current} / {total}',
    'nav.wizard_back': '← Back',
    'nav.wizard_next': 'Next →',

    // ── Bottom bar ─────────────────────────────────────────────────────
    'bottombar.price_incl_tax': 'incl. tax',
    'bottombar.price_excl_tax': 'excl. tax',
    'bottombar.cta': 'Add to cart',
    'bottombar.cta_disabled_title': 'Add-to-cart will be wired in a later phase.',
    'bottombar.cta_blocked_title': 'Resolve the listed errors to enable add-to-cart.',
    'bottombar.errors_more': '+{n} more',

    // ── Summary panel ──────────────────────────────────────────────────
    'summary.title': 'Summary',
    'summary.total': 'Total',
    'summary.sku': 'Article number',

    // ── Text / area ────────────────────────────────────────────────────
    'text.min': 'min {value}',
    'text.max': 'max {value}',
    'area.max_chars': 'max {value} characters',
    'select.placeholder': 'Please choose',

    // ── Calculated ─────────────────────────────────────────────────────
    'calc.badge': 'Calculated',
    'eval.result': 'Result: {value}',
    'combi.badge': 'Combination',
    'combi.empty': '(no fields)',
    'matrix.badge': 'Matrix',
    'matrix.dimensions': 'X: {x} ({opx}), Y: {y} ({opy})',
    'matrix.empty': '{x} × {y} (no values)',
    'matrix.overflow': '{rows} × {cols} total — preview limited to {prows} × {pcols}',
    'productattribute.badge': 'Product attribute',
    'productattribute.empty': 'no attribute selected',
    'webservice.badge': 'Webservice',
    'webservice.return_type': 'Return type',
    'webservice.path': 'Path',

    // ── Package ────────────────────────────────────────────────────────
    'package.badge': 'Package',
    'package.price': 'Package price',
    'package.activate': 'Activate package',
    'package.value_refs': '{n} linked characteristic references',
    'package.empty': 'Empty package.',

    // ── File upload ────────────────────────────────────────────────────
    'fileupload.dropzone': 'Drop file here or click to select',
    'fileupload.pick_or_drop': 'Choose file or drop here',
    'fileupload.uploading': 'Uploading…',
    'fileupload.remove': 'Remove {name}',
    'fileupload.min_files': 'min {n} file(s)',
    'fileupload.max_files': 'max {n} file(s)',
    'fileupload.max_size': 'max {size}',
    'fileupload.error_too_big': '"{name}" exceeds the maximum size of {max}.',
    'fileupload.error_wrong_type': '"{name}" has an unsupported file type.',
    'fileupload.error_too_many': 'You can upload at most {max} file(s).',
    'fileupload.error_no_endpoint': 'Upload endpoint not configured.',
    'fileupload.error_server': 'Server rejected the upload (HTTP {code}).',
    'fileupload.error_network': 'Upload failed: {msg}',

    // ── Chart ──────────────────────────────────────────────────────────
    'chart.placeholder': 'Chart preview — visualisation will follow in a later phase.',
    'chart.placeholder_with_type': 'Chart ({type}) — visualisation will follow in a later phase.',

    // ── Action button ──────────────────────────────────────────────────
    'actionbutton.default_label': 'Action',
    'actionbutton.disabled_title': 'Action button — will be wired in a later phase.',
    'actionbutton.post_title': 'POST → {url}',
    'actionbutton.busy': 'Sending…',
    'actionbutton.success': 'Successfully submitted.',
    'actionbutton.network_error': 'Submission failed: {message}',
    'actionbutton.no_endpoint': 'This action has no endpoint configured.',
    'actionbutton.no_handler': 'The page has no handler set up for this button.',

    // ── Validation ─────────────────────────────────────────────────────
    'validation.required': '{field} is required',
    'validation.numeric': '{field} must be a number',
    'validation.pattern': '{field} has an invalid format',
    'validation.min': 'Min: {value}',
    'validation.max': 'Max: {value}',
    'validation.min_length': 'At least {value} characters',
    'validation.max_length': 'At most {value} characters',
    'validation.validate-email': '{field} is not a valid e-mail address',
    'validation.validate-uri': '{field} is not a valid URL',
    'validation.validate-url': '{field} is not a valid URL',
    'validation.validate-zip': '{field} is not a valid postal code',
    'validation.validate-phoneLax': '{field} is not a valid phone number',
    'validation.validate-phone': '{field} is not a valid phone number',
    'validation.validate-digits': '{field} must be a whole number',
    'validation.validate-greater-than-zero': '{field} must be greater than zero',
    'validation.validate-zero-or-greater': '{field} must be zero or greater',
    'validation.validate-alpha': '{field} may only contain letters',
    'validation.validate-code': '{field} may only contain a–z, 0–9 and underscore',
    'validation.validate-lowercase': '{field} may only contain lowercase letters and digits',
    'validation.validate-uppercase': '{field} may only contain uppercase letters and digits',
    'validation.validate-alphanum': '{field} may only contain letters and digits',
    'validation.validate-data': '{field} may only contain letters, digits and underscore',
    'validation.validate-iban': '{field} is not a valid IBAN',
    'validation.validate-bic': '{field} is not a valid BIC',
    'validation.validate-vat': '{field} is not a valid VAT ID',
    'validation.captcha_required': 'Please solve the captcha before submitting.',
    'deeplink.create': 'Generate deep link',
    'deeplink.copied': 'Link copied — it opens this configuration.',
    'deeplink.created': 'Link created — copy it from the field.',
    'deeplink.failed': 'The link could not be created.',
    'validation.min_files': '{field}: please upload at least {n} files.',

    // ── Captcha ────────────────────────────────────────────────────────
    'captcha.loading': 'Loading captcha…',
    'captcha.load_failed': 'Captcha could not be loaded. Please reload the page.',
    'captcha.preview_placeholder': 'Captcha — loaded only in the live storefront, not in this preview.',

    // ── Listimage ──────────────────────────────────────────────────────
    'listimage.no_selection': 'No selection yet',
    'listimage.expand': 'Show options',
    'listimage.collapse': 'Hide options',
    'listimage.open_overlay': 'Open picker',

    // ── Unknown ────────────────────────────────────────────────────────
    'unknown.warning': 'Unknown characteristic type: {type} — not rendered by this bundle version.',
} as const;

export type MessageKey = keyof typeof en_US;

const de_DE: Record<MessageKey, string> = {
    'state.idle.detail': 'Kein Preview-Kontext — Bundle ist gemountet, wartet aber noch auf Daten.',
    'state.loading': 'Komponente wird geladen …',
    'state.error.title': 'Komponente konnte nicht geladen werden',
    'state.error.title_with_status': 'Komponente konnte nicht geladen werden (HTTP {status})',
    'state.error.token_expired': 'Token möglicherweise abgelaufen — Vorschau neu laden.',

    'fallback.component_title': 'Komponente {id}',
    'fallback.group_title': 'Gruppe {id}',

    'group.empty': 'Keine Gruppen in dieser Komponente.',
    'group.no_characteristics': 'Keine Merkmale in dieser Gruppe.',
    'characteristic.required_aria': 'Pflichtfeld',
    'characteristic.more_info_aria': 'Weitere Informationen',
    'characteristic.show_info': 'Details anzeigen',
    'characteristic.close': 'Schließen',
    'characteristic.no_values': 'Keine Auswahlmöglichkeiten.',
    'characteristic.no_images': 'Keine Bilder hinterlegt.',
    'characteristic.no_options': 'Keine Optionen.',
    'characteristic.no_content': 'Kein Inhalt hinterlegt.',
    'characteristic.aria_select': 'Auswahl',
    'characteristic.quantity': 'Menge',
    'characteristic.aria_quantity': '{label} Menge',

    'nav.tabs_aria': 'Gruppen',
    'nav.wizard_step': 'Schritt {current} / {total}',
    'nav.wizard_back': '← Zurück',
    'nav.wizard_next': 'Weiter →',

    'bottombar.price_incl_tax': 'inkl. MwSt.',
    'bottombar.price_excl_tax': 'zzgl. MwSt.',
    'bottombar.cta': 'In den Warenkorb',
    'bottombar.cta_disabled_title': 'Add-to-Cart wird in einer späteren Phase verdrahtet.',
    'bottombar.cta_blocked_title': 'Behebe die aufgeführten Fehler, um in den Warenkorb zu legen.',
    'bottombar.errors_more': '+{n} weitere',

    'summary.title': 'Zusammenfassung',
    'summary.total': 'Gesamt',
    'summary.sku': 'Artikelnummer',

    'text.min': 'min {value}',
    'text.max': 'max {value}',
    'area.max_chars': 'max {value} Zeichen',
    'select.placeholder': 'Bitte wählen',

    'calc.badge': 'Berechnet',
    'eval.result': 'Ergebnis: {value}',
    'combi.badge': 'Kombination',
    'combi.empty': '(keine Felder)',
    'matrix.badge': 'Matrix',
    'matrix.dimensions': 'X: {x} ({opx}), Y: {y} ({opy})',
    'matrix.empty': '{x} × {y} (keine Werte)',
    'matrix.overflow': '{rows} × {cols} insgesamt — Vorschau begrenzt auf {prows} × {pcols}',
    'productattribute.badge': 'Produkt-Attribut',
    'productattribute.empty': 'kein Attribut gewählt',
    'webservice.badge': 'Webservice',
    'webservice.return_type': 'Return-Type',
    'webservice.path': 'Pfad',

    'package.badge': 'Paket',
    'package.price': 'Paket-Preis',
    'package.activate': 'Paket aktivieren',
    'package.value_refs': '{n} verlinkte Merkmal-Referenzen',
    'package.empty': 'Leeres Paket.',

    'fileupload.dropzone': 'Datei hierher ziehen oder auswählen',
    'fileupload.pick_or_drop': 'Datei wählen oder hier ablegen',
    'fileupload.uploading': 'Wird hochgeladen…',
    'fileupload.remove': '{name} entfernen',
    'fileupload.min_files': 'min {n} Datei(en)',
    'fileupload.max_files': 'max {n} Datei(en)',
    'fileupload.max_size': 'max {size}',
    'fileupload.error_too_big': '„{name}" überschreitet die maximale Größe von {max}.',
    'fileupload.error_wrong_type': '„{name}" hat einen nicht unterstützten Dateityp.',
    'fileupload.error_too_many': 'Es können höchstens {max} Datei(en) hochgeladen werden.',
    'fileupload.error_no_endpoint': 'Upload-Endpunkt ist nicht konfiguriert.',
    'fileupload.error_server': 'Server hat den Upload abgelehnt (HTTP {code}).',
    'fileupload.error_network': 'Upload fehlgeschlagen: {msg}',

    'chart.placeholder': 'Chart-Vorschau — Visualisierung folgt in einer späteren Phase.',
    'chart.placeholder_with_type': 'Chart ({type}) — Visualisierung folgt in einer späteren Phase.',

    'actionbutton.default_label': 'Aktion',
    'actionbutton.disabled_title': 'Action-Button — wird in einer späteren Phase verdrahtet.',
    'actionbutton.post_title': 'POST → {url}',
    'actionbutton.busy': 'Sende …',
    'actionbutton.success': 'Erfolgreich übermittelt.',
    'actionbutton.network_error': 'Übermittlung fehlgeschlagen: {message}',
    'actionbutton.no_endpoint': 'Für diese Aktion ist kein Endpunkt konfiguriert.',
    'actionbutton.no_handler': 'Die Seite hat keine Verarbeitung für diesen Button eingerichtet.',

    'validation.required': '{field} ist ein Pflichtfeld',
    'validation.numeric': '{field} muss eine Zahl sein',
    'validation.pattern': '{field} hat ein ungültiges Format',
    'validation.min': 'Mindestens: {value}',
    'validation.max': 'Höchstens: {value}',
    'validation.min_length': 'Mindestens {value} Zeichen',
    'validation.max_length': 'Höchstens {value} Zeichen',
    'validation.validate-email': '{field} ist keine gültige E-Mail-Adresse',
    'validation.validate-uri': '{field} ist keine gültige URL',
    'validation.validate-url': '{field} ist keine gültige URL',
    'validation.validate-zip': '{field} ist keine gültige Postleitzahl',
    'validation.validate-phoneLax': '{field} ist keine gültige Telefonnummer',
    'validation.validate-phone': '{field} ist keine gültige Telefonnummer',
    'validation.validate-digits': '{field} muss eine ganze Zahl sein',
    'validation.validate-greater-than-zero': '{field} muss größer als Null sein',
    'validation.validate-zero-or-greater': '{field} muss Null oder größer sein',
    'validation.validate-alpha': '{field} darf nur Buchstaben enthalten',
    'validation.validate-code': '{field} darf nur a–z, 0–9 und Unterstrich enthalten',
    'validation.validate-lowercase': '{field} darf nur Kleinbuchstaben und Ziffern enthalten',
    'validation.validate-uppercase': '{field} darf nur Großbuchstaben und Ziffern enthalten',
    'validation.validate-alphanum': '{field} darf nur Buchstaben und Ziffern enthalten',
    'validation.validate-data': '{field} darf nur Buchstaben, Ziffern und Unterstrich enthalten',
    'validation.validate-iban': '{field} ist keine gültige IBAN',
    'validation.validate-bic': '{field} ist kein gültiger BIC',
    'validation.validate-vat': '{field} ist keine gültige USt-IdNr.',
    'validation.captcha_required': 'Bitte löse das Captcha vor dem Absenden.',
    'deeplink.create': 'Deep Link erzeugen',
    'deeplink.copied': 'Link kopiert – er öffnet genau diese Konfiguration.',
    'deeplink.created': 'Link erzeugt – kopiere ihn aus dem Feld.',
    'deeplink.failed': 'Der Link konnte nicht erzeugt werden.',
    'validation.min_files': '{field}: Bitte lade mindestens {n} Dateien hoch.',

    'captcha.loading': 'Captcha wird geladen …',
    'captcha.load_failed': 'Captcha konnte nicht geladen werden. Bitte Seite neu laden.',
    'captcha.preview_placeholder': 'Captcha — wird nur im echten Storefront geladen, nicht in dieser Vorschau.',

    'listimage.no_selection': 'Noch keine Auswahl',
    'listimage.expand': 'Optionen anzeigen',
    'listimage.collapse': 'Optionen ausblenden',
    'listimage.open_overlay': 'Auswahl öffnen',

    'unknown.warning': 'Unbekannter Merkmal-Typ: {type} — wird von dieser Bundle-Version nicht gerendert.',
};

const fr_FR: Record<MessageKey, string> = {
    'state.idle.detail': "Aucun contexte d'aperçu — bundle monté mais en attente de données.",
    'state.loading': 'Chargement du composant …',
    'state.error.title': 'Le composant n’a pas pu être chargé',
    'state.error.title_with_status': 'Le composant n’a pas pu être chargé (HTTP {status})',
    'state.error.token_expired': "Le jeton est peut-être expiré — rechargez l’aperçu.",

    'fallback.component_title': 'Composant {id}',
    'fallback.group_title': 'Groupe {id}',

    'group.empty': 'Aucun groupe dans ce composant.',
    'group.no_characteristics': 'Aucune caractéristique dans ce groupe.',
    'characteristic.required_aria': 'Champ obligatoire',
    'characteristic.more_info_aria': "Plus d'informations",
    'characteristic.show_info': 'Afficher les détails',
    'characteristic.close': 'Fermer',
    'characteristic.no_values': 'Aucune valeur disponible.',
    'characteristic.no_images': 'Aucune image configurée.',
    'characteristic.no_options': 'Aucune option.',
    'characteristic.no_content': 'Aucun contenu.',
    'characteristic.aria_select': 'Sélection',
    'characteristic.quantity': 'Quantité',
    'characteristic.aria_quantity': 'Quantité {label}',

    'nav.tabs_aria': 'Groupes',
    'nav.wizard_step': 'Étape {current} / {total}',
    'nav.wizard_back': '← Retour',
    'nav.wizard_next': 'Suivant →',

    'bottombar.price_incl_tax': 'TTC',
    'bottombar.price_excl_tax': 'HT',
    'bottombar.cta': 'Ajouter au panier',
    'bottombar.cta_disabled_title': 'L’ajout au panier sera disponible dans une phase ultérieure.',
    'bottombar.cta_blocked_title': 'Corrigez les erreurs listées pour activer l’ajout au panier.',
    'bottombar.errors_more': '+{n} autres',

    'summary.title': 'Récapitulatif',
    'summary.total': 'Total',
    'summary.sku': 'Référence',

    'text.min': 'min {value}',
    'text.max': 'max {value}',
    'area.max_chars': 'max {value} caractères',
    'select.placeholder': 'Veuillez choisir',

    'calc.badge': 'Calculé',
    'eval.result': 'Résultat : {value}',
    'combi.badge': 'Combinaison',
    'combi.empty': '(aucun champ)',
    'matrix.badge': 'Matrice',
    'matrix.dimensions': 'X : {x} ({opx}), Y : {y} ({opy})',
    'matrix.empty': '{x} × {y} (aucune valeur)',
    'matrix.overflow': '{rows} × {cols} au total — aperçu limité à {prows} × {pcols}',
    'productattribute.badge': 'Attribut produit',
    'productattribute.empty': 'aucun attribut sélectionné',
    'webservice.badge': 'Webservice',
    'webservice.return_type': 'Type de retour',
    'webservice.path': 'Chemin',

    'package.badge': 'Pack',
    'package.price': 'Prix du pack',
    'package.activate': 'Activer le pack',
    'package.value_refs': '{n} références de caractéristiques liées',
    'package.empty': 'Pack vide.',

    'fileupload.dropzone': 'Déposez un fichier ici ou cliquez pour sélectionner',
    'fileupload.pick_or_drop': 'Choisir un fichier ou déposer ici',
    'fileupload.uploading': 'Téléversement…',
    'fileupload.remove': 'Supprimer {name}',
    'fileupload.min_files': 'min {n} fichier(s)',
    'fileupload.max_files': 'max {n} fichier(s)',
    'fileupload.max_size': 'max {size}',
    'fileupload.error_too_big': '« {name} » dépasse la taille maximale de {max}.',
    'fileupload.error_wrong_type': '« {name} » a un type de fichier non pris en charge.',
    'fileupload.error_too_many': 'Vous pouvez téléverser au maximum {max} fichier(s).',
    'fileupload.error_no_endpoint': "Point d'envoi non configuré.",
    'fileupload.error_server': "Le serveur a refusé l'envoi (HTTP {code}).",
    'fileupload.error_network': 'Échec du téléversement : {msg}',

    'chart.placeholder': 'Aperçu du graphique — la visualisation arrivera dans une phase ultérieure.',
    'chart.placeholder_with_type':
        'Graphique ({type}) — la visualisation arrivera dans une phase ultérieure.',

    'actionbutton.default_label': 'Action',
    'actionbutton.disabled_title': 'Bouton d’action — sera disponible dans une phase ultérieure.',
    'actionbutton.post_title': 'POST → {url}',
    'actionbutton.busy': 'Envoi…',
    'actionbutton.success': 'Envoi réussi.',
    'actionbutton.network_error': 'Échec de l’envoi : {message}',
    'actionbutton.no_endpoint': 'Aucun point de terminaison n’est configuré pour cette action.',
    'actionbutton.no_handler': 'La page n’a pas de traitement configuré pour ce bouton.',

    'validation.required': '{field} est un champ obligatoire',
    'validation.numeric': '{field} doit être un nombre',
    'validation.pattern': '{field} a un format invalide',
    'validation.min': 'Min : {value}',
    'validation.max': 'Max : {value}',
    'validation.min_length': 'Au moins {value} caractères',
    'validation.max_length': 'Au plus {value} caractères',
    'validation.validate-email': '{field} n’est pas une adresse e-mail valide',
    'validation.validate-uri': '{field} n’est pas une URL valide',
    'validation.validate-url': '{field} n’est pas une URL valide',
    'validation.validate-zip': '{field} n’est pas un code postal valide',
    'validation.validate-phoneLax': '{field} n’est pas un numéro de téléphone valide',
    'validation.validate-phone': '{field} n’est pas un numéro de téléphone valide',
    'validation.validate-digits': '{field} doit être un nombre entier',
    'validation.validate-greater-than-zero': '{field} doit être supérieur à zéro',
    'validation.validate-zero-or-greater': '{field} doit être zéro ou supérieur',
    'validation.validate-alpha': '{field} ne peut contenir que des lettres',
    'validation.validate-code': '{field} ne peut contenir que a–z, 0–9 et le souligné',
    'validation.validate-lowercase': '{field} ne peut contenir que des lettres minuscules et des chiffres',
    'validation.validate-uppercase': '{field} ne peut contenir que des lettres majuscules et des chiffres',
    'validation.validate-alphanum': '{field} ne peut contenir que des lettres et des chiffres',
    'validation.validate-data': '{field} ne peut contenir que des lettres, chiffres et le souligné',
    'validation.validate-iban': '{field} n’est pas un IBAN valide',
    'validation.validate-bic': '{field} n’est pas un BIC valide',
    'validation.validate-vat': '{field} n’est pas un numéro de TVA valide',
    'validation.captcha_required': 'Veuillez résoudre le captcha avant d’envoyer.',
    'deeplink.create': 'Générer un lien profond',
    'deeplink.copied': 'Lien copié – il ouvre exactement cette configuration.',
    'deeplink.created': 'Lien créé – copiez-le depuis le champ.',
    'deeplink.failed': 'Le lien n’a pas pu être créé.',
    'validation.min_files': '{field} : veuillez téléverser au moins {n} fichiers.',

    'captcha.loading': 'Chargement du captcha …',
    'captcha.load_failed': 'Le captcha n’a pas pu être chargé. Veuillez recharger la page.',
    'captcha.preview_placeholder': 'Captcha — chargé uniquement dans la boutique en ligne, pas dans cet aperçu.',

    'listimage.no_selection': 'Aucune sélection',
    'listimage.expand': 'Afficher les options',
    'listimage.collapse': 'Masquer les options',
    'listimage.open_overlay': 'Ouvrir la sélection',

    'unknown.warning':
        'Type de caractéristique inconnu : {type} — non pris en charge par cette version du bundle.',
};

const es_ES: Record<MessageKey, string> = {
    'state.idle.detail':
        'Sin contexto de previsualización — bundle montado pero a la espera de datos.',
    'state.loading': 'Cargando componente …',
    'state.error.title': 'No se pudo cargar el componente',
    'state.error.title_with_status': 'No se pudo cargar el componente (HTTP {status})',
    'state.error.token_expired':
        'Es posible que el token haya caducado — recarga la previsualización.',

    'fallback.component_title': 'Componente {id}',
    'fallback.group_title': 'Grupo {id}',

    'group.empty': 'No hay grupos en este componente.',
    'group.no_characteristics': 'No hay características en este grupo.',
    'characteristic.required_aria': 'Campo obligatorio',
    'characteristic.more_info_aria': 'Más información',
    'characteristic.show_info': 'Mostrar detalles',
    'characteristic.close': 'Cerrar',
    'characteristic.no_values': 'No hay valores disponibles.',
    'characteristic.no_images': 'No hay imágenes configuradas.',
    'characteristic.no_options': 'Sin opciones.',
    'characteristic.no_content': 'Sin contenido.',
    'characteristic.aria_select': 'Selección',
    'characteristic.quantity': 'Cantidad',
    'characteristic.aria_quantity': 'Cantidad de {label}',

    'nav.tabs_aria': 'Grupos',
    'nav.wizard_step': 'Paso {current} / {total}',
    'nav.wizard_back': '← Atrás',
    'nav.wizard_next': 'Siguiente →',

    'bottombar.price_incl_tax': 'IVA incl.',
    'bottombar.price_excl_tax': 'IVA excl.',
    'bottombar.cta': 'Añadir al carrito',
    'bottombar.cta_disabled_title': 'La opción de añadir al carrito llegará en una fase posterior.',
    'bottombar.cta_blocked_title': 'Resuelve los errores mostrados para añadir al carrito.',
    'bottombar.errors_more': '+{n} más',

    'summary.title': 'Resumen',
    'summary.total': 'Total',
    'summary.sku': 'Referencia',

    'text.min': 'mín {value}',
    'text.max': 'máx {value}',
    'area.max_chars': 'máx {value} caracteres',
    'select.placeholder': 'Elige una opción',

    'calc.badge': 'Calculado',
    'eval.result': 'Resultado: {value}',
    'combi.badge': 'Combinación',
    'combi.empty': '(sin campos)',
    'matrix.badge': 'Matriz',
    'matrix.dimensions': 'X: {x} ({opx}), Y: {y} ({opy})',
    'matrix.empty': '{x} × {y} (sin valores)',
    'matrix.overflow': '{rows} × {cols} en total — vista limitada a {prows} × {pcols}',
    'productattribute.badge': 'Atributo de producto',
    'productattribute.empty': 'ningún atributo seleccionado',
    'webservice.badge': 'Webservice',
    'webservice.return_type': 'Tipo de retorno',
    'webservice.path': 'Ruta',

    'package.badge': 'Paquete',
    'package.price': 'Precio del paquete',
    'package.activate': 'Activar paquete',
    'package.value_refs': '{n} referencias a características vinculadas',
    'package.empty': 'Paquete vacío.',

    'fileupload.dropzone': 'Suelta un archivo aquí o haz clic para seleccionarlo',
    'fileupload.pick_or_drop': 'Elige un archivo o suéltalo aquí',
    'fileupload.uploading': 'Subiendo…',
    'fileupload.remove': 'Eliminar {name}',
    'fileupload.min_files': 'mín {n} archivo(s)',
    'fileupload.max_files': 'máx {n} archivo(s)',
    'fileupload.max_size': 'máx {size}',
    'fileupload.error_too_big': '«{name}» supera el tamaño máximo de {max}.',
    'fileupload.error_wrong_type': '«{name}» tiene un tipo de archivo no soportado.',
    'fileupload.error_too_many': 'Puedes subir como máximo {max} archivo(s).',
    'fileupload.error_no_endpoint': 'Endpoint de subida no configurado.',
    'fileupload.error_server': 'El servidor rechazó la subida (HTTP {code}).',
    'fileupload.error_network': 'Subida fallida: {msg}',

    'chart.placeholder': 'Vista previa del gráfico — la visualización llegará en una fase posterior.',
    'chart.placeholder_with_type':
        'Gráfico ({type}) — la visualisación llegará en una fase posterior.',

    'actionbutton.default_label': 'Acción',
    'actionbutton.disabled_title': 'Botón de acción — se conectará en una fase posterior.',
    'actionbutton.post_title': 'POST → {url}',
    'actionbutton.busy': 'Enviando…',
    'actionbutton.success': 'Enviado correctamente.',
    'actionbutton.network_error': 'Error al enviar: {message}',
    'actionbutton.no_endpoint': 'Esta acción no tiene un endpoint configurado.',
    'actionbutton.no_handler': 'La página no tiene ningún controlador configurado para este botón.',

    'validation.required': '{field} es un campo obligatorio',
    'validation.numeric': '{field} debe ser un número',
    'validation.pattern': '{field} tiene un formato no válido',
    'validation.min': 'Mín.: {value}',
    'validation.max': 'Máx.: {value}',
    'validation.min_length': 'Al menos {value} caracteres',
    'validation.max_length': 'Como máximo {value} caracteres',
    'validation.validate-email': '{field} no es una dirección de e-mail válida',
    'validation.validate-uri': '{field} no es una URL válida',
    'validation.validate-url': '{field} no es una URL válida',
    'validation.validate-zip': '{field} no es un código postal válido',
    'validation.validate-phoneLax': '{field} no es un número de teléfono válido',
    'validation.validate-phone': '{field} no es un número de teléfono válido',
    'validation.validate-digits': '{field} debe ser un número entero',
    'validation.validate-greater-than-zero': '{field} debe ser mayor que cero',
    'validation.validate-zero-or-greater': '{field} debe ser cero o mayor',
    'validation.validate-alpha': '{field} sólo puede contener letras',
    'validation.validate-code': '{field} sólo puede contener a–z, 0–9 y guión bajo',
    'validation.validate-lowercase': '{field} sólo puede contener minúsculas y dígitos',
    'validation.validate-uppercase': '{field} sólo puede contener mayúsculas y dígitos',
    'validation.validate-alphanum': '{field} sólo puede contener letras y dígitos',
    'validation.validate-data': '{field} sólo puede contener letras, dígitos y guión bajo',
    'validation.validate-iban': '{field} no es un IBAN válido',
    'validation.validate-bic': '{field} no es un BIC válido',
    'validation.validate-vat': '{field} no es un NIF/CIF válido',
    'validation.captcha_required': 'Por favor, resuelve el captcha antes de enviar.',
    'deeplink.create': 'Generar enlace profundo',
    'deeplink.copied': 'Enlace copiado: abre exactamente esta configuración.',
    'deeplink.created': 'Enlace creado: cópialo del campo.',
    'deeplink.failed': 'No se ha podido crear el enlace.',
    'validation.min_files': '{field}: sube al menos {n} archivos.',

    'captcha.loading': 'Cargando captcha …',
    'captcha.load_failed': 'No se pudo cargar el captcha. Por favor, recarga la página.',
    'captcha.preview_placeholder': 'Captcha — solo se carga en la tienda real, no en esta vista previa.',

    'listimage.no_selection': 'Aún sin selección',
    'listimage.expand': 'Mostrar opciones',
    'listimage.collapse': 'Ocultar opciones',
    'listimage.open_overlay': 'Abrir el selector',

    'unknown.warning':
        'Tipo de característica desconocido: {type} — no admitido por esta versión del bundle.',
};

const it_IT: Record<MessageKey, string> = {
    'state.idle.detail': 'Nessun contesto di anteprima — bundle montato, in attesa dei dati.',
    'state.loading': 'Caricamento del componente …',
    'state.error.title': 'Impossibile caricare il componente',
    'state.error.title_with_status': 'Impossibile caricare il componente (HTTP {status})',
    'state.error.token_expired': 'Il token potrebbe essere scaduto — ricarica l’anteprima.',

    'fallback.component_title': 'Componente {id}',
    'fallback.group_title': 'Gruppo {id}',

    'group.empty': 'Nessun gruppo in questo componente.',
    'group.no_characteristics': 'Nessuna caratteristica in questo gruppo.',
    'characteristic.required_aria': 'Campo obbligatorio',
    'characteristic.more_info_aria': 'Maggiori informazioni',
    'characteristic.show_info': 'Mostra dettagli',
    'characteristic.close': 'Chiudi',
    'characteristic.no_values': 'Nessun valore disponibile.',
    'characteristic.no_images': 'Nessuna immagine configurata.',
    'characteristic.no_options': 'Nessuna opzione.',
    'characteristic.no_content': 'Nessun contenuto.',
    'characteristic.aria_select': 'Selezione',
    'characteristic.quantity': 'Quantità',
    'characteristic.aria_quantity': 'Quantità {label}',

    'nav.tabs_aria': 'Gruppi',
    'nav.wizard_step': 'Passo {current} / {total}',
    'nav.wizard_back': '← Indietro',
    'nav.wizard_next': 'Avanti →',

    'bottombar.price_incl_tax': 'IVA inclusa',
    'bottombar.price_excl_tax': 'IVA esclusa',
    'bottombar.cta': 'Aggiungi al carrello',
    'bottombar.cta_disabled_title': 'L’aggiunta al carrello sarà disponibile in una fase successiva.',
    'bottombar.cta_blocked_title': 'Risolvi gli errori elencati per abilitare l’aggiunta al carrello.',
    'bottombar.errors_more': '+{n} altri',

    'summary.title': 'Riepilogo',
    'summary.total': 'Totale',
    'summary.sku': 'Codice articolo',

    'text.min': 'min {value}',
    'text.max': 'max {value}',
    'area.max_chars': 'max {value} caratteri',
    'select.placeholder': 'Seleziona un’opzione',

    'calc.badge': 'Calcolato',
    'eval.result': 'Risultato: {value}',
    'combi.badge': 'Combinazione',
    'combi.empty': '(nessun campo)',
    'matrix.badge': 'Matrice',
    'matrix.dimensions': 'X: {x} ({opx}), Y: {y} ({opy})',
    'matrix.empty': '{x} × {y} (nessun valore)',
    'matrix.overflow': '{rows} × {cols} in totale — anteprima limitata a {prows} × {pcols}',
    'productattribute.badge': 'Attributo prodotto',
    'productattribute.empty': 'nessun attributo selezionato',
    'webservice.badge': 'Webservice',
    'webservice.return_type': 'Tipo di ritorno',
    'webservice.path': 'Percorso',

    'package.badge': 'Pacchetto',
    'package.price': 'Prezzo del pacchetto',
    'package.activate': 'Attiva pacchetto',
    'package.value_refs': '{n} riferimenti a caratteristiche collegate',
    'package.empty': 'Pacchetto vuoto.',

    'fileupload.dropzone': 'Trascina qui un file o fai clic per selezionarlo',
    'fileupload.pick_or_drop': 'Scegli un file oppure trascinalo qui',
    'fileupload.uploading': 'Caricamento…',
    'fileupload.remove': 'Rimuovi {name}',
    'fileupload.min_files': 'min {n} file',
    'fileupload.max_files': 'max {n} file',
    'fileupload.max_size': 'max {size}',
    'fileupload.error_too_big': '«{name}» supera la dimensione massima di {max}.',
    'fileupload.error_wrong_type': '«{name}» ha un tipo di file non supportato.',
    'fileupload.error_too_many': 'Puoi caricare al massimo {max} file.',
    'fileupload.error_no_endpoint': 'Endpoint di caricamento non configurato.',
    'fileupload.error_server': 'Il server ha rifiutato il caricamento (HTTP {code}).',
    'fileupload.error_network': 'Caricamento fallito: {msg}',

    'chart.placeholder': 'Anteprima del grafico — la visualizzazione arriverà in una fase successiva.',
    'chart.placeholder_with_type':
        'Grafico ({type}) — la visualizzazione arriverà in una fase successiva.',

    'actionbutton.default_label': 'Azione',
    'actionbutton.disabled_title': 'Pulsante d’azione — sarà disponibile in una fase successiva.',
    'actionbutton.post_title': 'POST → {url}',
    'actionbutton.busy': 'Invio…',
    'actionbutton.success': 'Inviato con successo.',
    'actionbutton.network_error': 'Invio fallito: {message}',
    'actionbutton.no_endpoint': 'Per questa azione non è configurato alcun endpoint.',
    'actionbutton.no_handler': 'La pagina non ha un gestore configurato per questo pulsante.',

    'validation.required': '{field} è un campo obbligatorio',
    'validation.numeric': '{field} deve essere un numero',
    'validation.pattern': '{field} ha un formato non valido',
    'validation.min': 'Min: {value}',
    'validation.max': 'Max: {value}',
    'validation.min_length': 'Almeno {value} caratteri',
    'validation.max_length': 'Al massimo {value} caratteri',
    'validation.validate-email': '{field} non è un indirizzo e-mail valido',
    'validation.validate-uri': '{field} non è un URL valido',
    'validation.validate-url': '{field} non è un URL valido',
    'validation.validate-zip': '{field} non è un codice postale valido',
    'validation.validate-phoneLax': '{field} non è un numero di telefono valido',
    'validation.validate-phone': '{field} non è un numero di telefono valido',
    'validation.validate-digits': '{field} deve essere un numero intero',
    'validation.validate-greater-than-zero': '{field} deve essere maggiore di zero',
    'validation.validate-zero-or-greater': '{field} deve essere zero o maggiore',
    'validation.validate-alpha': '{field} può contenere solo lettere',
    'validation.validate-code': '{field} può contenere solo a–z, 0–9 e underscore',
    'validation.validate-lowercase': '{field} può contenere solo minuscole e cifre',
    'validation.validate-uppercase': '{field} può contenere solo maiuscole e cifre',
    'validation.validate-alphanum': '{field} può contenere solo lettere e cifre',
    'validation.validate-data': '{field} può contenere solo lettere, cifre e underscore',
    'validation.validate-iban': '{field} non è un IBAN valido',
    'validation.validate-bic': '{field} non è un BIC valido',
    'validation.validate-vat': '{field} non è una partita IVA valida',
    'validation.captcha_required': 'Risolvi il captcha prima di inviare.',
    'deeplink.create': 'Genera deep link',
    'deeplink.copied': 'Link copiato: apre esattamente questa configurazione.',
    'deeplink.created': 'Link creato: copialo dal campo.',
    'deeplink.failed': 'Impossibile creare il link.',
    'validation.min_files': '{field}: carica almeno {n} file.',

    'captcha.loading': 'Caricamento del captcha …',
    'captcha.load_failed': 'Impossibile caricare il captcha. Ricarica la pagina.',
    'captcha.preview_placeholder': 'Captcha — caricato solo nello storefront reale, non in questa anteprima.',

    'listimage.no_selection': 'Nessuna selezione',
    'listimage.expand': 'Mostra opzioni',
    'listimage.collapse': 'Nascondi opzioni',
    'listimage.open_overlay': 'Apri la selezione',

    'unknown.warning':
        'Tipo di caratteristica sconosciuto: {type} — non supportato da questa versione del bundle.',
};

const nl_NL: Record<MessageKey, string> = {
    'state.idle.detail': 'Geen preview-context — bundle is geladen, wacht nog op gegevens.',
    'state.loading': 'Component wordt geladen …',
    'state.error.title': 'Component kon niet worden geladen',
    'state.error.title_with_status': 'Component kon niet worden geladen (HTTP {status})',
    'state.error.token_expired': 'Token mogelijk verlopen — herlaad de preview.',

    'fallback.component_title': 'Component {id}',
    'fallback.group_title': 'Groep {id}',

    'group.empty': 'Geen groepen in dit component.',
    'group.no_characteristics': 'Geen kenmerken in deze groep.',
    'characteristic.required_aria': 'Verplicht veld',
    'characteristic.more_info_aria': 'Meer informatie',
    'characteristic.show_info': 'Details tonen',
    'characteristic.close': 'Sluiten',
    'characteristic.no_values': 'Geen waarden beschikbaar.',
    'characteristic.no_images': 'Geen afbeeldingen geconfigureerd.',
    'characteristic.no_options': 'Geen opties.',
    'characteristic.no_content': 'Geen inhoud.',
    'characteristic.aria_select': 'Selectie',
    'characteristic.quantity': 'Aantal',
    'characteristic.aria_quantity': 'Aantal {label}',

    'nav.tabs_aria': 'Groepen',
    'nav.wizard_step': 'Stap {current} / {total}',
    'nav.wizard_back': '← Terug',
    'nav.wizard_next': 'Volgende →',

    'bottombar.price_incl_tax': 'incl. btw',
    'bottombar.price_excl_tax': 'excl. btw',
    'bottombar.cta': 'In winkelwagen',
    'bottombar.cta_disabled_title': 'Toevoegen aan winkelwagen volgt in een latere fase.',
    'bottombar.cta_blocked_title': 'Los de getoonde fouten op om toevoegen aan winkelwagen mogelijk te maken.',
    'bottombar.errors_more': '+{n} meer',

    'summary.title': 'Samenvatting',
    'summary.total': 'Totaal',
    'summary.sku': 'Artikelnummer',

    'text.min': 'min {value}',
    'text.max': 'max {value}',
    'area.max_chars': 'max {value} tekens',
    'select.placeholder': 'Maak een keuze',

    'calc.badge': 'Berekend',
    'eval.result': 'Resultaat: {value}',
    'combi.badge': 'Combinatie',
    'combi.empty': '(geen velden)',
    'matrix.badge': 'Matrix',
    'matrix.dimensions': 'X: {x} ({opx}), Y: {y} ({opy})',
    'matrix.empty': '{x} × {y} (geen waarden)',
    'matrix.overflow': '{rows} × {cols} totaal — preview beperkt tot {prows} × {pcols}',
    'productattribute.badge': 'Productattribuut',
    'productattribute.empty': 'geen attribuut gekozen',
    'webservice.badge': 'Webservice',
    'webservice.return_type': 'Retourtype',
    'webservice.path': 'Pad',

    'package.badge': 'Pakket',
    'package.price': 'Pakketprijs',
    'package.activate': 'Pakket activeren',
    'package.value_refs': '{n} gekoppelde kenmerkverwijzingen',
    'package.empty': 'Leeg pakket.',

    'fileupload.dropzone': 'Sleep een bestand hierheen of klik om te kiezen',
    'fileupload.pick_or_drop': 'Kies een bestand of sleep het hierheen',
    'fileupload.uploading': 'Bezig met uploaden…',
    'fileupload.remove': '{name} verwijderen',
    'fileupload.min_files': 'min {n} bestand(en)',
    'fileupload.max_files': 'max {n} bestand(en)',
    'fileupload.max_size': 'max {size}',
    'fileupload.error_too_big': '«{name}» overschrijdt de maximale grootte van {max}.',
    'fileupload.error_wrong_type': '«{name}» heeft een niet-ondersteund bestandstype.',
    'fileupload.error_too_many': 'Je kunt maximaal {max} bestand(en) uploaden.',
    'fileupload.error_no_endpoint': 'Upload-endpoint niet geconfigureerd.',
    'fileupload.error_server': 'Server heeft de upload geweigerd (HTTP {code}).',
    'fileupload.error_network': 'Upload mislukt: {msg}',

    'chart.placeholder': 'Diagram-preview — visualisatie volgt in een latere fase.',
    'chart.placeholder_with_type':
        'Diagram ({type}) — visualisatie volgt in een latere fase.',

    'actionbutton.default_label': 'Actie',
    'actionbutton.disabled_title': 'Actieknop — wordt in een latere fase aangesloten.',
    'actionbutton.post_title': 'POST → {url}',
    'actionbutton.busy': 'Bezig met verzenden…',
    'actionbutton.success': 'Succesvol verzonden.',
    'actionbutton.network_error': 'Verzenden mislukt: {message}',
    'actionbutton.no_endpoint': 'Voor deze actie is geen endpoint geconfigureerd.',
    'actionbutton.no_handler': 'De pagina heeft geen verwerking voor deze knop ingesteld.',

    'validation.required': '{field} is een verplicht veld',
    'validation.numeric': '{field} moet een getal zijn',
    'validation.pattern': '{field} heeft een ongeldige indeling',
    'validation.min': 'Min: {value}',
    'validation.max': 'Max: {value}',
    'validation.min_length': 'Minstens {value} tekens',
    'validation.max_length': 'Maximaal {value} tekens',
    'validation.validate-email': '{field} is geen geldig e-mailadres',
    'validation.validate-uri': '{field} is geen geldige URL',
    'validation.validate-url': '{field} is geen geldige URL',
    'validation.validate-zip': '{field} is geen geldige postcode',
    'validation.validate-phoneLax': '{field} is geen geldig telefoonnummer',
    'validation.validate-phone': '{field} is geen geldig telefoonnummer',
    'validation.validate-digits': '{field} moet een geheel getal zijn',
    'validation.validate-greater-than-zero': '{field} moet groter dan nul zijn',
    'validation.validate-zero-or-greater': '{field} moet nul of groter zijn',
    'validation.validate-alpha': '{field} mag alleen letters bevatten',
    'validation.validate-code': '{field} mag alleen a–z, 0–9 en underscore bevatten',
    'validation.validate-lowercase': '{field} mag alleen kleine letters en cijfers bevatten',
    'validation.validate-uppercase': '{field} mag alleen hoofdletters en cijfers bevatten',
    'validation.validate-alphanum': '{field} mag alleen letters en cijfers bevatten',
    'validation.validate-data': '{field} mag alleen letters, cijfers en underscore bevatten',
    'validation.validate-iban': '{field} is geen geldig IBAN',
    'validation.validate-bic': '{field} is geen geldige BIC',
    'validation.validate-vat': '{field} is geen geldig btw-nummer',
    'validation.captcha_required': 'Los het captcha op voordat je verstuurt.',
    'deeplink.create': 'Deeplink maken',
    'deeplink.copied': 'Link gekopieerd – hij opent precies deze configuratie.',
    'deeplink.created': 'Link gemaakt – kopieer hem uit het veld.',
    'deeplink.failed': 'De link kon niet worden gemaakt.',
    'validation.min_files': '{field}: upload minstens {n} bestanden.',

    'captcha.loading': 'Captcha wordt geladen …',
    'captcha.load_failed': 'Captcha kon niet worden geladen. Herlaad de pagina.',
    'captcha.preview_placeholder': 'Captcha — wordt alleen in de echte storefront geladen, niet in deze preview.',

    'listimage.no_selection': 'Nog geen selectie',
    'listimage.expand': 'Opties tonen',
    'listimage.collapse': 'Opties verbergen',
    'listimage.open_overlay': 'Selectie openen',

    'unknown.warning':
        'Onbekend kenmerktype: {type} — wordt door deze bundle-versie niet weergegeven.',
};

export const messages: Record<Locale, Record<MessageKey, string>> = {
    en_US,
    de_DE,
    fr_FR,
    es_ES,
    it_IT,
    nl_NL,
};
